class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this._moved) return;
    this._moved = true;
    // Theme editor settings changes re-render the section and insert a fresh
    // <cart-drawer>, but the previous one already escaped to <body> (see below)
    // so Shopify's section reload never removes it - drop it here or stale
    // duplicates (with stale settings) pile up and stay clickable.
    document.querySelectorAll('cart-drawer').forEach((el) => {
      if (el !== this) el.remove();
    });
    // Move to <body> so the drawer escapes the sticky header's stacking context
    // (.section-header.shopify-section-group-header-group, z-index: 3) — otherwise
    // no z-index on this element can beat body-level overlays like the PDP sidebar
    // panel (.pdp-sidebar.is-open, z-index: 100000), which appends itself to <body>.
    document.body.appendChild(this);
  }

  setHeaderCartIconAccessibility() {
    // Both the default and alternate headers render an icon with this id (only one
    // is visible at a time - the other is hidden via CSS, not removed from the DOM),
    // so querySelectorAll is required here: querySelector would only ever wire up
    // whichever one happens to come first in the markup, leaving the other's icon to
    // fall back to its plain <a href> and navigate to the cart page instead of opening
    // this drawer.
    const cartLinks = document.querySelectorAll('#cart-icon-bubble');
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('cart-drawer-open');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const onParallaxMobile =
      window.innerWidth <= 1023 &&
      !!document.querySelector('.product-paralax .product__info-wrapper');

    this.classList.add('is-closing');
    this.classList.remove('active');
    document.body.classList.remove('cart-drawer-open');

    const inner = this.querySelector('.drawer__inner');
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      this.classList.remove('is-closing');
      removeTrapFocus(onParallaxMobile ? null : this.activeElement);
      document.body.classList.remove('overflow-hidden');
      this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    };

    if (inner) {
      inner.addEventListener('transitionend', cleanup, { once: true });
    }
    setTimeout(cleanup, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, skipOpen = false) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-init related products swiper after DOM replacement
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer-render' } }));

    if (skipOpen) return;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sectionId = document.getElementById('cart-drawer-section')?.dataset.sectionId || 'cart_drawer';
    return [
      {
        id: sectionId,
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sectionId = document.getElementById('cart-drawer-section')?.dataset.sectionId || 'cart-drawer';
    return [
      {
        id: 'CartDrawer',
        section: sectionId,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
